import _ from 'lodash'
import type {
  ICanvasEngineProperties,
  CanvasEngineConstructor,
  CanvasEngineSoftLayer,
  Vector2d,
} from './types'
import {
  CANVAS_HANDLE_MODE,
  CANVAS_STATE,
  CANVAS_FOCUS_MODE,
  CANVAS_CREATE_METHOD,
  SHAPES,
  CANVAS_HANDLE_EVENT,
} from './const'
import { CanvasEngineEntity } from './entity'
import { EventManager } from './eventManager'
import { TileLayer } from './tileLayer'
import { createRenderer } from './backend/createRenderer'
import type { IGroup, IImage, IPointerEvent, IRenderer, IStage } from './backend/IRenderer'
import type { StaticCacheOptions } from './backend/types'

// 앱(네이티브 WebView) 환경 감지
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isAppWebView = !!(window as any)?.Capacitor?.isNativePlatform?.()

export class CanvasEngineProperties implements ICanvasEngineProperties {
  options = {
    basicCreationWidth: 10,
    basicCreationHeight: 10,
  }

  // 이벤트 관리자
  protected eventManager = EventManager.getInstance()

  // 레이어 캐싱 관련 속성 추가
  protected useCacheOnDrag: boolean = true
  public cacheQuality: number = 1 // Android WebView에서는 더 낮은 품질 사용
  // 엔티티 캐시의 hit 캔버스 해상도(scene 대비 배율). hit은 색상키 판정용이라 저해상도여도 된다.
  // 실측(태블릿, 캐시 282MP): 전량 캐싱 5.4→2.7s, 제스처 끝 hit 재구축 166→106ms, 터치 선택 성공률 동일.
  // 터치 여백(hitStrokeWidth 10~50px, 점 hit 반경 ×8)이 4px 단위 판정보다 훨씬 넓어 선택이 유지된다.
  public hitCacheQuality: number = 0.25

  protected state: CANVAS_STATE = CANVAS_STATE.IDLE
  protected canvasHandleMode: CANVAS_HANDLE_MODE = CANVAS_HANDLE_MODE.DEFAULT
  protected selectionMode: CANVAS_FOCUS_MODE = CANVAS_FOCUS_MODE.SINGLE

  protected createMode: CANVAS_CREATE_METHOD = CANVAS_CREATE_METHOD.MOUSE_UP
  protected selectedCreateEntityType: SHAPES = SHAPES.POINT

  // Shape을 선택한 경우 자동으로 CreateMode 등을 해제
  protected autoModeChangeWhenShapeSelected: boolean = true
  protected setPositionSpeed: number = 1350
  protected isAnimating: boolean = false
  protected __isEntitiyCreating: boolean = false
  // async 콜백(onCreateShape/onCreateBrush) 진행 중 여부 — 중복 생성 방지용
  protected __isCreateCallbackPending: boolean = false
  // 모든 shape관련 스타일은 기본값에 유저가 설정가능한 배율: 0.4, 0.7, 1, 2.2, 3 을 곱하여 사용
  // shape 관련 상수
  protected originBalloonSize: number = 15 // 벌룬의 기본 크기 (반지름)
  protected originBalloonThickness: number = 1 // 벌룬 테두리의 기본 굵기
  protected originBalloonFontSize: number = 20 // 벌룬 글씨의 기본 크기
  protected originConnectedLineWidth: number = 1 // 연결선의 기본 굵기
  protected originPointSize: number = 3 // 점의 크기 (지름, SHAPES.POINT의 크기를 결정할때 사용, 외곽선이 있으므로 실제크기보다 작음)
  protected originAreaLineWidth: number = 1 // 영역 선 굵기 (shape의 테두리와 balloon-shape 연결선, balloon 테두리 제외)

  //생성에만 관여하는 속성들
  public forCreateBalloonScale: number = 1 // 생성 시 도형 크기 스케일
  public forCreateConnectedLineThicknessScale: number = 1 // 생성 시 도형 연결선 굵기 스케일
  public forCreateBalloonFontScale: number = 1 // 생성 시 도형 벌룬 글씨 크기 스케일
  public forCreatePointShapeScale: number = 1 // 생성 시 도형 점의 크기 스케일
  public forCreateAreaBorderThicknessScale: number = 1 // 생성 시 도형 영역 선 굵기 스케일

  //생성에만 관여하는 색상들
  public forCreateBalloonFontColor: string = '#000000' // 생성 시 도형 색상
  public forCreateBalloonFillColor: string = '#000000' // 생성 시 도형 내부 색상
  public forCreateBalloonBorderColor: string = '#000000' // 생성 시 도형 텍스트 색상
  public forCreateBalloonFillOpacity: number = 1 // 생성 시 도형 채우기 투명도

  //글로벌에만 관여하는 속성들
  public globalBalloonScale: number = 1 // 전역 벌룬 크기 스케일
  public globalConnectedLineThicknessScale: number = 1 // 전역 연결선 굵기 스케일
  public globalBalloonFontScale: number = 1 // 전역 벌룬 글씨 크기 스케일
  public globalPointShapeScale: number = 1 // 전역 점의 크기 스케일
  public globalAreaBorderThicknessScale: number = 1 // 전역 영역 선 굵기 스케일

  //글로벌에만 관여하는 색상들
  public globalBalloonFontColor: string = '#000000' // 전역 벌룬 글씨 색상
  public globalBalloonFillColor: string = '#000000' // 전역 벌룬 채우기 색상
  public globalBalloonBorderColor: string = '#000000' // 전역 벌룬 테두리 색상
  public globalBalloonFillOpacity: number = 1 // 전역 벌룬 채우기 투명도

  //변화된 스케일에 따라 계산된 값
  get balloonSize(): number {
    return this.originBalloonSize * 2 * this.globalBalloonScale
  }
  get balloonThickness(): number {
    return this.originBalloonThickness * this.globalBalloonScale
  }
  get balloonFontSize(): number {
    return this.originBalloonFontSize * this.globalBalloonFontScale
  }
  get connectedLineWidth(): number {
    return this.originConnectedLineWidth * this.globalConnectedLineThicknessScale
  }
  get pointSize(): number {
    return this.originPointSize * this.globalPointShapeScale
  }
  get areaLineWidth(): number {
    return this.originAreaLineWidth * this.globalAreaBorderThicknessScale
  }
  get balloonFontColor(): string {
    return this.globalBalloonFontColor
  }
  get balloonFillColor(): string {
    return this.globalBalloonFillColor
  }
  get balloonBorderColor(): string {
    return this.globalBalloonBorderColor
  }
  get balloonFillOpacity(): number {
    return this.globalBalloonFillOpacity
  }
  get getCacheQuality(): number {
    return this.cacheQuality
  }

  public balloonScale: number = 1
  public connectedLineThicknessScale: number = 1
  public balloonFontScale: number = 1
  public pointShapeScale: number = 1
  public areaBorderThicknessScale: number = 1

  // Fill 패턴 관련 설정
  public fillPatternLineWidth: number = 2 // 채우기 사선의 굵기
  public fillPatternSpacing: number = 10 // 채우기 사선의 간격

  protected isAllFloorNumbering: boolean = false

  get isEntityCreating(): boolean {
    return this.__isEntitiyCreating || this.canvasHandleMode == CANVAS_HANDLE_MODE.CREATE
  }

  // 렌더러. 스테이지·레이어는 renderer.stage / renderer.layers.{background,system} 핸들로 쓴다
  protected renderer: IRenderer

  guideLineColor: string = '#D3D3D3'

  public isMobile: boolean = false
  public isMobileSelectMode: boolean = false
  // 핀치줌 진행 중 여부 (터치 추적에서 갱신; 스테이지 드래그 종료 시 hit 복원 판단에도 사용)
  public isPinchZooming = false
  // 스테이지 핸들(= renderer.stage). destroy() 뒤 null — 지연 콜백의 "엔진 살아 있음" 판정에 쓴다
  protected stage: IStage | null
  protected entities: CanvasEngineEntity[] = []

  protected preventLayerData: string = ''
  protected selectedLayerId: string = ''
  protected selectedShapeId: string = ''
  protected selectedEntity: CanvasEngineEntity | undefined

  // 자유 그리기 모드에서 사용할 색상
  protected drawingColor: string = '#000000'
  // 자유 그리기 모드에서 사용할 두께
  protected drawingThickness: number = 5
  // 지우개 모드 여부
  protected isEraserMode: boolean = false
  // 지우개 크기
  protected eraserThickness: number = 84
  // 자유 그리기 형태 (붓/직선/사각형)
  protected brushDrawMode: 'free' | 'line' | 'rect' = 'free'

  protected minZoom: number = 0.12
  protected maxZoom: number = 10

  protected backgroundWidth: number = 0
  protected backgroundHeight: number = 0
  protected averageBackgroundSize: number = 0
  protected backgroundRotation: 0 | 90 | 180 | 270 = 0
  protected backgroundImage: IImage | undefined
  // 도면의 해상도를 동적으로 조정하기 위한 타일 레이어
  protected tileLayer: TileLayer | undefined

  // entity에서 Count 되는 항목 관리용
  protected index = 1
  protected allFloorIndex = 1
  // NO_SHAPE 결함이 차지하는 번호 (이 번호를 건너뜀)
  protected reservedIndices: Set<number> = new Set()
  // 미등록 결함 연결 시 생성되는 엔티티에 즉시 표시할 번호
  protected pendingCreateBalloonText: string | null = null
  /**
   * EntityId 와 EntityIndex를 매핑하는 맵
   */
  protected indexMap = {} as Record<string, string>

  protected entityLayerMap = {} as Record<string, string[]>
  protected softLayerNameMap = {} as Record<string, string>
  protected defaultLayerId = ''

  events = {
    onDeleteLayer: (_layerId: string): void => {},
    onUpdateLayer: (_layers: CanvasEngineSoftLayer[]): void => {},
    onSelectLayer: (_layerId: string): void => {},
    onSelectShape: (_shapeId: string): void => {},
    onGrabShapes: (
      _shapeIds: string[],
      _grabbedBox: { x: number; y: number; width: number; height: number } | null,
    ): void => {},
    onEntityTransform: (_shapeId: string): void => {},
    onEntityDragEnd: (_shapeId: string): void => {},
    onTransformShapes: (_shapeIds: string[]): void => {},
    onCreateLayer: (_layer: CanvasEngineSoftLayer): void => {},
    onModeChanged: (_mode: CANVAS_HANDLE_MODE): void => {},
    onStateChanged: (_state: CANVAS_STATE): void => {},
    onLostFocus: (): void => {},
    onCreateShape: (_shape: CanvasEngineEntity): void => {},
    onChangeColorShape: (
      _shapeId: string,
      _color: string,
      _type: 'new' | 'previous',
      _originalColor: string,
    ): void => {},
    onCreateBrush: (_shape: CanvasEngineEntity): void => {},
    onCreateModeChanged: (_createMethod: CANVAS_CREATE_METHOD): void => {},
    onViewportChanged: (_viewport: { x: number; y: number; zoom: number }): void => {},
    onBackgroundSizeChanged: (_width: number, _height: number): void => {},
    onIndexChanged: (_data: Record<string, string>): void => {},
    onShowButton: (_container: HTMLElement, _shapeId: string): void => {},
    onHideButton: (_shapeId: string): void => {},
    onCopyShape: (_shapeId: string): void => {},
    onDeleteShapes: (_shapeIds: string[]): void => {},
    onCombineShape: (_shapeIds: string[]): void => {},
    onBrushErased: async (
      _modifiedEntityIds: string[],
      _deletedEntities: Record<string, number>,
    ): Promise<void> => {},
    onDirectionDrawStateChanged: (_state: {
      showMagnifyingGlass: boolean
      currentX: number
      currentY: number
      magPosition: string
    }): void => {},
  }

  public getStage = (): IStage => {
    return this.renderer.stage
  }
  /** 렌더러 — 엔티티 부속이 노드 팩토리·스테이지/레이어 핸들을 쓰는 통로 */
  public getRenderer = (): IRenderer => {
    return this.renderer
  }
  public getMinZoom = (): number => {
    return this.minZoom
  }
  public getMaxZoom = (): number => {
    return this.maxZoom
  }
  public getAverageBackgroundSize = (): number => {
    return this.averageBackgroundSize
  }

  constructor(arg: CanvasEngineConstructor) {
    const renderer = createRenderer({
      kind: arg.renderer,
      container: arg.container,
      width: arg.width,
      height: arg.height,
    })
    this.renderer = renderer
    this.stage = renderer.stage
    // 휠 제스처 중 포인터 입력은 Konva 리스너(content 요소)보다 먼저 캡처 단계에서 처리
    // (캔버스 밖 DOM 버튼 — 출력 등 — 클릭도 휠 제스처를 즉시 끝내야 하므로 window 캡처로 등록)
    window.addEventListener('pointerdown', this.flushWheelGesture, true)
    window.addEventListener('mousedown', this.flushWheelGesture, true)
    window.addEventListener('touchstart', this.flushWheelGesture, true)
    // 제스처 밖 배율 변경(setViewport·포커스 애니메이션·fitView 등)도 멈춘 뒤 캐시 해상도를 다시 맞춘다
    this.renderer.stage.on('scaleXChange.cacheRes', this.scheduleCacheResolutionCheck)

    // 앱(WebView) 환경 최적화
    if (isAppWebView) {
      this.optimizeForWebView(true)
    }

    // 시스템 이벤트 설정
    this.setupDefaultSystemEvents()
    this.enablePan()
  }

  /**
   * 모든 모드에서 공통으로 필요한 시스템 이벤트를 설정합니다.
   */
  protected setupDefaultSystemEvents(): void {
    this.eventManager.addEvent(
      this.renderer.stage,
      CANVAS_HANDLE_EVENT.PINCH_ZOOM_TOUCH_MOVE,
      () => {
        this.events.onViewportChanged({
          x: this.renderer.stage.x(),
          y: this.renderer.stage.y(),
          zoom: this.renderer.stage.scaleX(),
        })
      },
    )

    this.eventManager.addEvent(
      this.renderer.stage,
      CANVAS_HANDLE_EVENT.PINCH_ZOOM_TOUCH_END,
      () => {
        this.events.onViewportChanged({
          x: this.renderer.stage.x(),
          y: this.renderer.stage.y(),
          zoom: this.renderer.stage.scaleX(),
        })
      },
    )

    this.eventManager.addEvent(
      this.renderer.stage,
      CANVAS_HANDLE_EVENT.SYSTEM_VIEWPORT_CHANGED_DRAGMOVE,
      () => {
        this.entities.forEach((entity: CanvasEngineEntity) => {
          if (this.selectedEntity && this.selectedEntity.getId() != entity.getId()) {
            entity.node.listening(false)
          }
        })
        this.events.onViewportChanged({
          x: this.renderer.stage.x(),
          y: this.renderer.stage.y(),
          zoom: this.renderer.stage.scaleX(),
        })
      },
    )

    this.eventManager.addEvent(
      this.renderer.stage,
      CANVAS_HANDLE_EVENT.SYSTEM_VIEWPORT_CHANGED_DRAGMOVE_END,
      () => {
        this.entities.forEach((entity: CanvasEngineEntity) => {
          entity.node.listening(true)
        })
      },
    )

    this.eventManager.addEvent(
      this.renderer.stage,
      CANVAS_HANDLE_EVENT.SYSTEM_VIEWPORT_CHANGED_WHEEL,
      () => {
        this.events.onViewportChanged({
          x: this.renderer.stage.x(),
          y: this.renderer.stage.y(),
          zoom: this.renderer.stage.scaleX(),
        })
      },
    )

    // 마우스가 스테이지를 벗어날 때 기본 커서로 복원
    this.eventManager.addEvent(
      this.renderer.stage,
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_OUT,
      () => {
        if (this.selectedEntity && this.selectedEntity.isTripod()) return
        this.renderer.stage.container().style.cursor = 'default'
      },
    )
  }

  // WebView 최적화 설정 메서드 추가
  public optimizeForWebView(isWebView: boolean = true): void {
    if (isWebView) {
      // WebView에 최적화된 설정
      this.useCacheOnDrag = false // 드래그 중 캐시 비활성화
      // this.cacheQuality = 1; // 낮은 캐시 품질
    } else {
      // 일반 브라우저용 설정
      this.useCacheOnDrag = true
      // this.cacheQuality = 2;
    }
  }

  getIndex = (shapeId: string): string => {
    return this.indexMap[shapeId]
  }

  getBackgroundWidth = (): number => {
    return this.backgroundWidth
  }
  getBackgroundHeight = (): number => {
    return this.backgroundHeight
  }
  setStageSize = (width: number, height: number): void => {
    this.renderer.stage.width(width)
    this.renderer.stage.height(height)
  }

  toLocalX = (x: number): number => {
    return (x - this.renderer.stage.x()) * (1 / this.renderer.stage.scaleX())
  }
  toLocalY = (y: number): number => {
    return (y - this.renderer.stage.y()) * (1 / this.renderer.stage.scaleY())
  }

  setState = (state: CANVAS_STATE): void => {
    this.state = state
    this.events.onStateChanged(this.state)
  }

  isDrawing = (): boolean => {
    return this.state == CANVAS_STATE.DRAWING
  }

  setDrawingColor = (color: string): void => {
    this.drawingColor = color
  }

  setDrawingThickness = (thickness: number): void => {
    this.drawingThickness = thickness < 0.1 ? 0.1 : thickness
  }

  setEraserMode = (enabled: boolean): void => {
    this.isEraserMode = enabled
  }

  getEraserMode = (): boolean => {
    return this.isEraserMode
  }

  setEraserThickness = (thickness: number): void => {
    this.eraserThickness = thickness < 0.1 ? 0.1 : thickness
  }

  setBrushDrawMode = (mode: 'free' | 'line' | 'rect'): void => {
    this.brushDrawMode = mode
  }

  getBrushDrawMode = (): 'free' | 'line' | 'rect' => {
    return this.brushDrawMode
  }

  getDrawingConfig = (): {
    color: string
    thickness: number
  } => {
    return {
      color: this.drawingColor,
      thickness: this.drawingThickness,
    }
  }

  getHandleMode(): CANVAS_HANDLE_MODE | undefined {
    return this.canvasHandleMode
  }

  updateAllEntitiesOpacity(opacity: number, isRestore: boolean = false): void {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (isRestore) {
        entity.restoreOpacity()
      }
      entity.forceOpacity(opacity)
    })
    const firstEntity = this.entities[0]
    if (firstEntity) {
      firstEntity.node.getLayer()?.batchDraw()
    }
  }

  changeCanvasHandleMode(mode: CANVAS_HANDLE_MODE): void {
    const stage = this.renderer.stage
    this.eventManager.removeAllEvents(stage)
    this.canvasHandleMode = mode

    // 기본 시스템 이벤트 재설정
    // 시스템 이벤트 전체를 제거하기때문에 기본 이벤트 재설정 필요
    this.setupDefaultSystemEvents()

    switch (mode) {
      case CANVAS_HANDLE_MODE.EDIT:
        this.disablePan()
        //this.enableDragComponent();
        break
      case CANVAS_HANDLE_MODE.DRAW:
        this.disablePan()
        this.disableDragComponent()

        break
      case CANVAS_HANDLE_MODE.CREATE:
        this.disablePan()
        this.disableDragComponent()
        this.updateAllEntitiesOpacity(0.3)
        break
      case CANVAS_HANDLE_MODE.DIRECTION_DRAW:
        this.disablePan()
        this.disableDragComponent()
        this.updateAllEntitiesOpacity(0.3, true)
        break
      case CANVAS_HANDLE_MODE.DEFAULT:
        this.enablePan()
        this.entities.forEach((entity: CanvasEngineEntity) => {
          entity.restoreOpacity()
        })

        break
      case CANVAS_HANDLE_MODE.SELECTION:
        this.disablePan()
        this.disableDragComponent()
        break
      // 자동정렬모드인 경우 shape 선택만 막음 (selectShape에서 컨트롤)
      case CANVAS_HANDLE_MODE.AUTO_ALIGN:
        this.enablePan()
        break
      default:
        //this.enablePan()
        //   this.enableDragComponent();
        break
    }
    this.events.onModeChanged(this.canvasHandleMode)
  }

  getCreateMode = (): CANVAS_CREATE_METHOD => {
    return this.createMode
  }

  setEventListener = <K extends keyof typeof this.events>(
    eventName: K,
    handler: (typeof this.events)[K],
  ): void => {
    switch (eventName) {
      case 'onEntityTransform':
        this.events.onEntityTransform = (shapeId: string): void => {
          this.isEntityTransformed = true
          ;(handler as typeof this.events.onEntityTransform)(shapeId)
        }
        break
      default:
        this.events[eventName] = handler
        break
    }
  }

  setIsMobile = (isMobile: boolean): void => {
    this.isMobile = isMobile
  }

  disablePan = (): void => {
    const stage = this.renderer.stage
    const panEvents = [
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_DOWN,
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_TOUCH_START,
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_UP,
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_TOUCH_END,
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_LEAVE,
    ]

    // 해당 이벤트들 모두 제거
    this.eventManager.removeEvents(stage, panEvents)
    this.renderer.stage.container().style.cursor = 'default'
  }

  /**
   * 연속 조작 동안 전 레이어의 hit 캔버스 렌더를 생략 (scene 렌더 비용만 남김)
   * — 조작 중에는 클릭/탭 hit-test가 발생하지 않으므로 안전.
   *   반드시 조작 종료 시 resumeLayerHitDetection으로 복원해야 함 (멱등).
   */
  protected suspendLayerHitDetection(): void {
    const hints = this.renderer.hints
    if (hints.isHitSuspended()) return
    hints.suspendHit()
    this.beginGestureBrushCache()
  }

  /**
   * hit 캔버스 렌더 복원 + 조작 중 생략된 hit를 최신 변환으로 재구축
   * @param immediate true면 동기 draw로 즉시 재구축 (직후 hit-test가 예정된 경우)
   */
  protected resumeLayerHitDetection(immediate: boolean = false): void {
    // 브러시 임시 캐시는 hit 상태와 무관하게 항상 먼저 원복
    this.endGestureBrushCache()
    const hints = this.renderer.hints
    if (!hints.isHitSuspended()) return
    if (!this.stage) return
    hints.resumeHit(immediate)
    // 진입 캐싱 중 핀치(엔티티 listening=false) 등으로 건너뛴 엔티티를 제스처가 끝난 뒤 보충 캐싱하고
    // 바뀐 배율·화면 위치에 맞춰 캐시 해상도를 다시 맞춘다(같은 일을 하는 배율 변경 예약은 취소)
    // (엔티티 listening 복원이 이 호출 뒤에 이뤄지므로 다음 틱에 수행)
    if (this._cacheResTimer) {
      clearTimeout(this._cacheResTimer)
      this._cacheResTimer = null
    }
    setTimeout(() => {
      if (this.stage?.container()) this.deferredCacheEntities(true)
    }, 0)
  }

  // ---- 휠 줌 제스처 ----
  // 휠은 시작/종료 이벤트가 없으므로 첫 휠에서 조작 모드로 들어가고 마지막 휠 후 디바운스로 종료한다.
  // 디바운스 창 안에서 클릭/터치가 오면 캡처 단계에서 동기 복원해 낡은 hit로 잘못 선택되는 일을 막는다.
  private __wheelResumeTimer: ReturnType<typeof setTimeout> | null = null
  private readonly WHEEL_GESTURE_IDLE_MS = 200

  protected beginWheelGesture(): void {
    this.suspendLayerHitDetection()
    if (this.__wheelResumeTimer) clearTimeout(this.__wheelResumeTimer)
    this.__wheelResumeTimer = setTimeout(() => {
      this.__wheelResumeTimer = null
      this.resumeLayerHitDetection()
    }, this.WHEEL_GESTURE_IDLE_MS)
  }

  // 휠 디바운스 창 안의 포인터 입력: Konva가 hit-test하기 전에 동기 복원
  protected flushWheelGesture = (): void => {
    if (!this.__wheelResumeTimer) return
    clearTimeout(this.__wheelResumeTimer)
    this.__wheelResumeTimer = null
    this.resumeLayerHitDetection(true)
  }

  // ---- 브러시 스냅샷 (제스처 중 표시용, 세션 내 재사용) ----
  // 팬/핀치/휠 동안 브러시 엔티티들을 스냅샷 1장으로 대신 그린다(구현은 renderer.hints — Konva는 숨김 홀더 + 캐시 그룹).
  // 스냅샷은 브러시 집합/기하/스타일이 바뀔 때만 다시 만든다 — 제스처마다 재래스터화하면
  // 저사양에서 제스처 시작마다 ~300ms가 멈춰 짧은 플릭이 죽는다 (실기기 프로파일 확인).
  // 제스처가 끝나면 브러시를 원래 z-순서로 즉시 원복하므로 편집·지우개·필터·선택 경로와 충돌하지 않는다.
  // 어떤 엔티티를 넘길지(레이어에 직접 속한, 보이는 브러시)는 엔진 정책이다.

  // 레이어에 직접 속한, 보이는 브러시 엔티티
  private getGestureBrushes(): IGroup[] {
    return this.entities
      .filter(
        (entity: CanvasEngineEntity) =>
          entity.isBrush() &&
          entity.node.visible() &&
          entity.node.getParent() === this.renderer.layers.background,
      )
      .map(entity => this.entityNode(entity))
  }

  protected destroyBrushSnapshot(): void {
    this.renderer.hints.dropGestureSnapshot()
  }

  // 층 진입 캐싱 직후 등, 첫 제스처가 멈추지 않도록 스냅샷을 미리 준비
  protected prepareBrushSnapshot(): void {
    if (!this.stage?.container()) return
    this.renderer.hints.prepareGestureSnapshot(() => this.getGestureBrushes())
  }

  private beginGestureBrushCache(): void {
    this.renderer.hints.beginGesture(this.getGestureBrushes())
  }

  protected endGestureBrushCache(): void {
    this.renderer.hints.endGesture()
  }

  // ---- 엔티티 정적(비트맵 캐시) 처리 — 수단은 renderer.hints, 배치·예산·세대는 아래 캐시 정책 ----

  /** 엔티티 루트 노드 핸들 */
  protected entityNode(entity: CanvasEngineEntity): IGroup {
    return entity.node
  }

  /**
   * 엔티티를 비트맵 캐시로 굽는다(기존 캐시는 비우고 다시). listening 가드는 호출 측 책임.
   * pixelRatio를 주지 않으면 캐시 해상도 정책(화면 배율·위치·상한)으로 정하고, 상한상 0.1 미만이면 굽지 않는다.
   */
  public markEntityStatic(
    entity: CanvasEngineEntity,
    opts: Partial<StaticCacheOptions> = {},
  ): void {
    const offset = opts.offset ?? entity.getCacheHitOffset()
    const pixelRatio =
      opts.pixelRatio ?? this.planEntityCache(entity, this._cacheDesiredRatio, offset)?.ratio
    const node = this.entityNode(entity)
    const hints = this.renderer.hints
    if (hints.isStatic(node)) hints.unmarkStatic(node)
    if (pixelRatio === undefined) return
    hints.markStatic(node, {
      pixelRatio,
      // hit 캔버스는 저해상도(hitCacheQuality)로, 상한에 걸린 대형 엔티티는 scene 배율 이하로 제한
      // (대형 엔티티에서 수십 MB hit 캔버스 할당 → 저사양 프리즈/OOM 방지)
      hitPixelRatio: opts.hitPixelRatio ?? Math.min(pixelRatio, this.hitCacheQuality),
      // false면 빗금 패턴 이미지가 최근접 보간돼 확대 시 계단이 생긴다(캐싱 비용 차이 없음 — A21s 실측)
      smoothing: opts.smoothing ?? true,
      offset,
    })
    this._staticRatio.set(entity, pixelRatio)
  }

  // ---- 캐시 해상도(scene pixelRatio) 정책 ----
  // 캐시 비트맵은 월드 좌표 기준이라 화면 배율×DPR보다 해상도가 낮으면 확대돼 흐려진다
  // (배율 1 고정이면 A21s 줌 2에서 3.5배 확대). 화면 근처 엔티티만 현재 배율에 맞는 정수 단계(1~4)로 굽고,
  // 화면에서 멀어진 엔티티는 기본 배율(cacheQuality)로 되돌려 비트맵 메모리를 화면 크기 수준으로 묶는다.
  // A21s 실측(실층 153): 배율 1→4에서 전량 캐싱 2.6→3.2s·팬 9→12ms로 거의 같고(엔티티당 고정비 지배)
  // 비트맵 메모리만 r²로 는다(15→222MB) — 그래서 배율은 화면 근처로 한정하고 상한을 둔다.
  private readonly CACHE_RATIO_MAX = 4
  // 엔티티당 비트맵 상한 — Firefox에서 단일 cache()가 28초+ 블로킹하던 과대 엔티티 방어 겸 메모리 상한
  private readonly CACHE_MAX_PIXELS = 4_000_000
  // 기본 배율을 넘는 비트맵 합계 상한(약 48MB) — 근처 엔티티가 넘기면 목표 배율을 한 단계씩 낮추고,
  // 중간 구간에 남은 고배율 비트맵까지 합쳐 넘기면 중간 구간을 기본 배율로 내린다.
  // 8MP는 밀집 구역(A21s 줌 1·PC 줌 2)에서 배율 1로 떨어져 흐렸고, 12MP는 16MP와 육안 차이가 거의 없었다
  private readonly CACHE_HIRES_TOTAL_PIXELS = 12_000_000
  // 화면 크기 대비 여유: NEAR 안이면 목표 배율, FAR 밖이면 기본 배율로 강등, 사이는 현 상태 유지(팬 왕복 재캐시 방지)
  private readonly CACHE_NEAR_MARGIN = 0.25
  private readonly CACHE_FAR_MARGIN = 2
  private readonly CACHE_RES_IDLE_MS = 250
  private _cacheDesiredRatio = 1
  private _staticRatio = new WeakMap<CanvasEngineEntity, number>()
  private _cacheResTimer: ReturnType<typeof setTimeout> | null = null

  /** 화면 배율×DPR을 덮는 정수 배율(기본 배율 ~ CACHE_RATIO_MAX) */
  protected screenCacheRatio(): number {
    const need = (this.renderer.stage.scaleX() || 1) * (window.devicePixelRatio || 1)
    return Math.min(this.CACHE_RATIO_MAX, Math.max(this.cacheQuality, Math.ceil(need - 0.01)))
  }

  /**
   * 엔티티 하나의 캐시 계획: 화면 근접 구간(0 근처·1 중간·2 먼 곳), 목표 배율(엔티티당 상한 적용), 기준 픽셀 수.
   * 목표 배율이 0.1 미만(비정상적으로 거대한 엔티티)이면 null — 캐시하지 않는다.
   */
  private planEntityCache(
    entity: CanvasEngineEntity,
    desired: number,
    offset: number = entity.getCacheHitOffset(),
  ): { zone: 0 | 1 | 2; ratio: number; basePixels: number } | null {
    const stage = this.renderer.stage
    const scale = stage.scaleX() || 1
    const w = stage.width()
    const h = stage.height()
    const r = entity.node.getClientRect()
    const outside = (m: number): boolean =>
      r.x + r.width < -w * m || r.x > w * (1 + m) || r.y + r.height < -h * m || r.y > h * (1 + m)
    const zone = !outside(this.CACHE_NEAR_MARGIN) ? 0 : outside(this.CACHE_FAR_MARGIN) ? 2 : 1
    const basePixels = (r.width / scale + offset * 2) * (r.height / scale + offset * 2)
    let ratio =
      zone === 0
        ? desired
        : zone === 2
          ? this.cacheQuality
          : (this.staticRatioOf(entity) ?? this.cacheQuality)
    if (basePixels * ratio * ratio > this.CACHE_MAX_PIXELS)
      ratio = Math.sqrt(this.CACHE_MAX_PIXELS / basePixels)
    if (ratio < 0.1) return null
    return { zone, ratio, basePixels }
  }

  private staticRatioOf(entity: CanvasEngineEntity): number | undefined {
    return this.isEntityStatic(entity)
      ? (this._staticRatio.get(entity) ?? this.cacheQuality)
      : undefined
  }

  /** 이미 캐시된 엔티티를 계획 배율로 다시 구울지 — 올림은 즉시, 근처 내림은 절반 이하일 때만(줌 미세 조정 왕복 방지) */
  private needsCacheRatioChange(
    entity: CanvasEngineEntity,
    plan: { zone: 0 | 1 | 2; ratio: number; forceRatio?: boolean },
  ): boolean {
    const current = this.staticRatioOf(entity)
    if (current === undefined) return false
    if (plan.ratio > current + 0.01) return true
    if (plan.forceRatio) return current > plan.ratio + 0.01
    if (plan.zone === 2) return current > plan.ratio + 0.01
    if (plan.zone === 0) return current >= plan.ratio * 2
    return false
  }

  /**
   * 후보 엔티티들의 캐시 계획을 세운다: 목표 배율을 고배율 합계 상한에 맞추고(_cacheDesiredRatio 갱신),
   * 근처 엔티티가 먼저 오도록 정렬한다.
   */
  private planCacheResolution(candidates: CanvasEngineEntity[]): Array<{
    entity: CanvasEngineEntity
    zone: 0 | 1 | 2
    ratio: number
    basePixels: number
    forceRatio: boolean
  }> {
    let desired = this.screenCacheRatio()
    const plans = candidates.flatMap(entity => {
      const plan = this.planEntityCache(entity, desired)
      return plan ? [{ entity, forceRatio: false, ...plan }] : []
    })
    const pixelsAt = (p: { basePixels: number }, ratio: number): number =>
      ratio > this.cacheQuality ? Math.min(p.basePixels * ratio * ratio, this.CACHE_MAX_PIXELS) : 0
    const nearTotal = (d: number): number =>
      plans.reduce((sum, p) => (p.zone === 0 ? sum + pixelsAt(p, d) : sum), 0)
    while (desired > this.cacheQuality && nearTotal(desired) > this.CACHE_HIRES_TOTAL_PIXELS)
      desired--
    for (const p of plans) {
      if (p.zone === 0) p.ratio = Math.min(desired, Math.sqrt(this.CACHE_MAX_PIXELS / p.basePixels))
    }
    // 이번 판정 뒤 실제로 남을 배율: 다시 굽는 엔티티는 계획 배율, 그대로 두는 엔티티(중간 구간·근처 내림 유예)는 현재 배율
    const kept = (p: (typeof plans)[number]): number => {
      const current = this.staticRatioOf(p.entity)
      return current !== undefined && !this.needsCacheRatioChange(p.entity, p) ? current : p.ratio
    }
    const hiresTotal = (): number => plans.reduce((sum, p) => sum + pixelsAt(p, kept(p)), 0)
    // 상한을 넘으면 중간 구간 → 근처의 내림 유예 순으로 기본·목표 배율로 내린다
    if (hiresTotal() > this.CACHE_HIRES_TOTAL_PIXELS) {
      for (const p of plans) {
        if (p.zone !== 1) continue
        p.zone = 2
        p.ratio = Math.min(this.cacheQuality, Math.sqrt(this.CACHE_MAX_PIXELS / p.basePixels))
      }
    }
    if (hiresTotal() > this.CACHE_HIRES_TOTAL_PIXELS) {
      for (const p of plans) if (p.zone === 0) p.forceRatio = true
    }
    this._cacheDesiredRatio = desired
    return plans.sort((a, b) => a.zone - b.zone)
  }

  /** 제스처 밖 배율 변경이 멈추면 보충 캐싱(해상도 재판정 포함). 제스처 중이면 종료 시 resume이 대신 수행 */
  protected scheduleCacheResolutionCheck = (): void => {
    if (this._cacheResTimer) clearTimeout(this._cacheResTimer)
    this._cacheResTimer = setTimeout(() => {
      this._cacheResTimer = null
      if (!this.stage?.container() || this.renderer.hints.isHitSuspended()) return
      this.deferredCacheEntities(true)
    }, this.CACHE_RES_IDLE_MS)
  }

  public isEntityStatic(entity: CanvasEngineEntity): boolean {
    return this.renderer.hints.isStatic(this.entityNode(entity))
  }

  public unmarkEntityStatic(entity: CanvasEngineEntity): void {
    this.renderer.hints.unmarkStatic(this.entityNode(entity))
  }

  enablePan = (): void => {
    const stage = this.renderer.stage

    // 패닝 관련 이벤트 모두 제거 후 다시 설정
    const panEvents = [
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_DOWN,
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_TOUCH_START,
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_UP,
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_TOUCH_END,
      CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_LEAVE,
    ]

    this.eventManager.removeEvents(stage, panEvents)

    // 마우스 다운/터치 시작 이벤트
    const mouseDownEvents = `${CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_TOUCH_START}`
    this.eventManager.addEventFromString(stage, mouseDownEvents, (e: IPointerEvent) => {
      // 마우스 중앙 버튼(button === 1)인 경우에만 드래그 활성화
      const isTouchEvent = typeof TouchEvent !== 'undefined' && e.evt instanceof TouchEvent
      const useMouseMiddleButton = !isTouchEvent && (e.evt as MouseEvent).button === 1
      // 모바일 다중선택 모드에서는 터치 패닝 비활성화 (그랩박스 드래그를 위해)
      if (isTouchEvent && this.isMobileSelectMode) return
      if (!(useMouseMiddleButton || isTouchEvent) || this.isEntityCreating) return
      // draggable은 화면을 바꾸지 않는다 — Konva가 속성 변경마다 거는 전체 다시 그리기(A21s 도형 많은 층 ≈45ms)를 막는다
      this.renderer.hints.withoutAutoDraw(() => this.renderer.stage.draggable(true))
      // 마우스 커서를 grab으로 변경
      this.renderer.stage.container().style.cursor = 'grabbing'
    })

    // 마우스 업/터치 종료 이벤트
    const mouseUpEvents = `${CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_UP} ${CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_TOUCH_END}`
    this.eventManager.addEventFromString(stage, mouseUpEvents, (e: IPointerEvent) => {
      // 마우스 버튼을 놓으면 드래그 비활성화
      const isTouchEvent = typeof TouchEvent !== 'undefined' && e.evt instanceof TouchEvent
      const useMouseMiddleButton = !isTouchEvent && (e.evt as MouseEvent).button === 1
      if (!(useMouseMiddleButton || isTouchEvent) || this.isEntityCreating) return

      this.renderer.hints.withoutAutoDraw(() => this.renderer.stage.draggable(false))
      // 마우스 커서를 원래대로 복원
      this.renderer.stage.container().style.cursor = 'default'
    })

    // 마우스 이탈 이벤트
    this.eventManager.addEvent(stage, CANVAS_HANDLE_EVENT.SYSTEM_PANNING_STAGE_MOUSE_LEAVE, () => {
      this.renderer.hints.withoutAutoDraw(() => this.renderer.stage.draggable(false))
      // 마우스 커서를 원래대로 복원
      this.renderer.stage.container().style.cursor = 'default'
    })

    // 스테이지 팬(한 손가락 드래그/휠 버튼 드래그) 중 hit 캔버스 렌더 생략
    // — hitOnDragEnabled=true라 드래그 중에도 hit가 매 프레임 그려지므로 스테이지 자체 드래그에서만 끈다
    const view = this.renderer.stage
    view.off('.stagePanHit')
    view.on('dragstart.stagePanHit', (e: IPointerEvent) => {
      if (e.target === view) this.suspendLayerHitDetection()
    })
    view.on('dragend.stagePanHit', (e: IPointerEvent) => {
      // 핀치 시작 시 stopDrag()로 발생하는 dragend는 무시 — 핀치 종료 경로가 복원한다
      if (e.target === view && !this.isPinchZooming) this.resumeLayerHitDetection()
    })
  }

  disableDragComponent = (): void => {
    this.renderer.layers.background.getChildren().forEach(item => {
      item.draggable(false)
    })
  }
  /*
    enableDragComponent = () => {
        for(const layer of this.layers) {
            layer.layer.getChildren().forEach(item => {
                item.draggable(true)
            })
        }
    }
    */

  getStageSize = (): { width: number; height: number } => {
    const view = this.renderer.stage
    return {
      width: view.width() * (1 / view.scaleX()),
      height: view.height() * (1 / view.scaleY()),
    }
  }
  /**
   * 스테이지(뷰포트)의 화면 픽셀 크기(CSS px). getStageSize()는 scale로 나눈 월드 단위라 다르다.
   * 바깥에서 getStage().width()/height()를 직접 읽지 않도록 하는 경계 API.
   */
  getViewportSize = (): { width: number; height: number } => {
    return { width: this.renderer.stage.width(), height: this.renderer.stage.height() }
  }
  getPointerPosition = (): Vector2d => {
    const pos = this.renderer.getPointerPosition()
    if (!pos)
      return {
        x: 0,
        y: 0,
      }
    const scale = this.renderer.stage.scaleX()
    const stagePos = this.renderer.stage.position() // stage의 현재 위치
    const returnPos = {
      x: (pos.x - stagePos.x) / scale,
      y: (pos.y - stagePos.y) / scale,
    }
    return returnPos
  }

  protected initStageEvent = (): void => {
    const stage = this.renderer.stage

    // 시스템 이벤트를 제외한 모든 이벤트 제거
    for (const event of Object.values(CANVAS_HANDLE_EVENT)) {
      if (event.endsWith('_system')) continue
      this.eventManager.removeEvent(stage, event)
    }
  }

  // Entity가 변형된 경우
  protected isEntityTransformed = false
  protected isIndexChanged = false
  protected hasNoCache = true
  protected cacheBatchSize = 10
  // 지연 캐싱 취소용 세대 카운터 (층 전환 시 이전 캐싱 중단)
  private _cacheGeneration = 0

  // 브러시는 캐시 대상에서 제외 — 획이 도면을 가로질러 바운딩박스가 커서 캐시 비트맵이 거대해지고,
  // 매 프레임 대형 drawImage 블릿이 저사양 기기에서 벡터 렌더보다 훨씬 느림 (실기기 비교로 확인)
  protected isCacheableEntity(entity: CanvasEngineEntity): boolean {
    return !entity.isBrush()
  }

  // 의도적으로 캐시를 비워 둔 엔티티(선택 중 — 편집 중 내부 변화를 즉시 그려야 함). grab 모드는 하위에서 확장
  protected isCacheExcluded(entity: CanvasEngineEntity): boolean {
    return entity.isSelected
  }

  public async refreshCache(): Promise<void> {
    // 1. 업데이트 대상 필터링 (가시성 체크 추가로 연산량 최소화)
    // listening(false) 상태에서 캐시를 구우면 hit 캔버스가 빈 채로 구워져(stale)
    // listening 복구 후에도 선택 불가가 되므로 제외 — 복구 후 배치 캐싱이 다시 굽는다
    const entitiesToUpdate = this.entities.filter((entity: CanvasEngineEntity) => {
      if (!entity.node.listening()) return false
      if (!this.isCacheableEntity(entity)) return false
      if (this.isCacheExcluded(entity)) return false
      const isVisible = entity.node.visible?.() ?? true // 화면에 보이는 것만 우선 처리
      const isCached = this.isEntityStatic(entity)
      return isVisible && (!isCached || this.isEntityTransformed)
    })

    if (entitiesToUpdate.length === 0) return

    // 2. 배치 설정 (한 번에 처리할 개수)
    const BATCH_SIZE = this.cacheBatchSize // 사양에 따라 5~10 사이 조절
    const totalEntities = entitiesToUpdate.length

    for (let i = 0; i < totalEntities; i += BATCH_SIZE) {
      const chunk = entitiesToUpdate.slice(i, i + BATCH_SIZE)

      // 현재 배치(Batch) 처리
      await Promise.all(
        chunk.map(async entity => {
          try {
            // 실행 시점 재확인 — 배치 사이에 listening이 꺼질 수 있음 (stale hit 방지)
            if (!entity.node.listening()) return
            this.markEntityStatic(entity)
          } catch (error) {
            console.warn(`캐시 업데이트 실패: ${entity.getId()}`, error)
          }
        }),
      )

      // 3. [핵심] 배치 사이의 '숨쉬기' 구간
      // 메인 스레드 점유를 잠시 해제하여 브라우저가 화면을 갱신(Rendering)하고 사용자 입력을 처리하게 함
      if (i + BATCH_SIZE < totalEntities) {
        await new Promise(resolve => requestAnimationFrame(resolve))
        // 또는 아주 짧은 지연이 필요하면: await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }
  }

  /**
   * 비동기 배치 캐싱
   * @param onlyIds 지정 시 해당 id의 엔티티만 재캐싱 (번호 변경분 등 부분 갱신).
   *                이 모드에서는 isEntityTransformed 플래그를 변경하지 않아
   *                전량 갱신(refreshCache 등) 경로의 판정에 영향을 주지 않는다.
   */
  public async refreshAsyncCache(onlyIds?: Set<string>): Promise<void> {
    const changedOnly = onlyIds !== undefined
    const targets = this.entities.filter((entity: CanvasEngineEntity) => {
      // listening(false) 상태에서 구운 캐시는 hit이 비어 선택 불가(stale)가 되므로 제외
      if (!entity.node.listening()) return false
      if (!this.isCacheableEntity(entity)) return false
      // 선택/grab 중 엔티티는 의도적으로 미캐시 상태 (편집 중 내부 변화를 즉시 그려야 함)
      if (this.isCacheExcluded(entity)) return false
      if (!(entity.node.visible?.() ?? true)) return false
      // 변경분 모드에서도 미캐시 가시 엔티티는 포함 (숨김 해제된 엔티티가 벡터 렌더로 남지 않도록)
      if (changedOnly) {
        return onlyIds.has(entity.getId()) || !this.isEntityStatic(entity)
      }
      return !this.isEntityStatic(entity) || this.isEntityTransformed
    })

    if (onlyIds) {
      // 번호가 바뀌었지만 지금 재캐싱할 수 없는(숨김·listening 꺼짐) 엔티티는 낡은 비트맵을 비워
      // 다시 보일 때 미캐시로 판정돼 재캐싱되게 한다 (전량 갱신 플래그 없이도 stale 캐시 방지)
      const targetSet = new Set(targets)
      for (const entity of this.entities) {
        if (onlyIds.has(entity.getId()) && !targetSet.has(entity) && this.isEntityStatic(entity)) {
          this.unmarkEntityStatic(entity)
        }
      }
    }

    if (targets.length === 0) {
      if (!changedOnly) this.isEntityTransformed = false
      return
    }
    const FRAME_BUDGET = 12
    let currentIndex = 0

    const process = (): Promise<void> => {
      return new Promise(resolve => {
        const execute = (): void => {
          const startTime = performance.now()

          while (currentIndex < targets.length) {
            const entity = targets[currentIndex]

            try {
              // 실행 시점 재확인 — 프레임 분할 사이에 listening이 꺼질 수 있음 (stale hit 방지)
              if (entity.node.listening()) {
                this.markEntityStatic(entity)
              }
            } catch (e) {
              console.warn(`Cache failed: ${entity.getId()}`, e)
            }

            currentIndex++

            if (performance.now() - startTime > FRAME_BUDGET) {
              setTimeout(execute, 0)
              return
            }
          }

          this.renderer.requestDraw(this.renderer.layers.background)
          if (!changedOnly) this.isEntityTransformed = false
          resolve()
        }

        execute()
      })
    }

    await process()
  }

  /**
   * fn 동안 노드 추가·속성 변경이 레이어 그리기를 예약하지 않는다(엔티티 일괄 생성 조각용).
   * 호출 측이 끝에 한 번 그려야 한다
   */
  public withoutAutoDraw<T>(fn: () => T): T {
    return this.renderer.hints.withoutAutoDraw(fn)
  }

  /** 엔티티·시스템 레이어 그리기 예약(withoutAutoDraw로 미룬 그리기를 호출 측이 한 번 할 때) */
  public requestEntityDraw(): void {
    this.renderer.requestDraw(this.renderer.layers.background)
    this.renderer.requestDraw(this.renderer.layers.system)
  }

  /** 엔티티 하나의 그리기 내부 값(변환·보이기 등)을 미리 채운다 — 층 진입 첫 그리기 전 조각 처리용 */
  public warmEntityDrawCache(entity: CanvasEngineEntity): void {
    this.renderer.hints.warmDrawCaches(this.entityNode(entity))
  }

  /**
   * 예약된 배율 변경 보충 캐싱과 진행 중인 나눠 캐싱을 그만둔다. 층 진입 import 직전용 — 배경 단계의 뷰포트 변경이
   * 예약한 보충 캐싱이 import 도중에 돌면 반쯤 들어온 엔티티를 두 번 그리고 굽는다(A21s 도형 많은 층 49·63ms).
   * 진입 끝의 전체 캐싱이 모든 엔티티를 다시 다룬다
   */
  public cancelPendingCacheWork(): void {
    if (this._cacheResTimer) {
      clearTimeout(this._cacheResTimer)
      this._cacheResTimer = null
    }
    ++this._cacheGeneration
  }

  // 먼저 batchDraw로 즉시 화면을 표시한 뒤, 프레임 단위로 캐시를 생성
  /**
   * @param onlyUncached true면 아직 캐시되지 않은 엔티티와 화면 배율·위치상 해상도를 바꿔야 하는 엔티티만 대상
   *                     (제스처 종료 후 보충 캐싱·해상도 재판정용)
   * @param afterPendingDraw 호출 측이 막 그리기를 예약했으면(층 진입 첫 그리기) 그 프레임 뒤에 시작.
   *                         isStale이 참이 되면(다른 층 로드가 시작됨) 시작하지 않는다
   */
  public deferredCacheEntities(
    onlyUncached: boolean = false,
    afterPendingDraw?: { isStale: () => boolean },
  ): Promise<void> {
    const generation = ++this._cacheGeneration
    if (afterPendingDraw) {
      // 계획·첫 조각이 첫 그리기 프레임에 묶이지 않도록 그 프레임이 지난 뒤 시작하고, 처음 그리기 예약은
      // 생략한다(따로 예약하면 미캐시 벡터 전체를 한 번 더 그린다)
      return new Promise<void>(resolve => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (this._cacheGeneration !== generation || afterPendingDraw.isStale()) resolve()
            else void this.runDeferredCache(generation, onlyUncached).then(resolve)
          }, 0)
        })
      })
    }
    this.renderer.requestDraw(this.renderer.layers.background)
    return this.runDeferredCache(generation, onlyUncached)
  }

  private runDeferredCache(generation: number, onlyUncached: boolean): Promise<void> {
    // listening(false) 상태에서 구운 캐시는 hit이 비어 선택 불가(stale)가 되므로 제외
    const candidates = this.entities.filter(
      (entity: CanvasEngineEntity) =>
        entity.node.listening() &&
        this.isCacheableEntity(entity) &&
        !this.isCacheExcluded(entity) &&
        (entity.node.visible?.() ?? true),
    )
    // 화면 근처 엔티티가 먼저 오도록 정렬된 계획(목표 배율은 근처 합계 상한 반영)
    const targets = this.planCacheResolution(candidates).filter(
      plan =>
        !onlyUncached ||
        !this.isEntityStatic(plan.entity) ||
        this.needsCacheRatioChange(plan.entity, plan),
    )
    // 보충 캐싱(onlyUncached)은 이미 캐시된 엔티티를 건드리지 않으므로 변형 플래그를 소거하면 안 됨
    // — 소거하면 변형 후 아직 refreshCache가 돌지 않은 엔티티가 옛 비트맵으로 남는다
    if (targets.length === 0) {
      if (!onlyUncached) this.isEntityTransformed = false
      return Promise.resolve()
    }
    let currentIndex = 0
    const FRAME_BUDGET = 14

    return new Promise<void>(resolve => {
      const execute = (): void => {
        // 층 전환 등으로 새 캐싱이 시작되면 이전 루프 중단
        if (this._cacheGeneration !== generation) {
          resolve()
          return
        }

        const startTime = performance.now()
        // 한 조각(FRAME_BUDGET)을 굽고 남은 게 있으면 true
        const runSlice = (): boolean => {
          while (currentIndex < targets.length) {
            const { entity, ratio } = targets[currentIndex]
            currentIndex++

            try {
              // 실행 시점 재확인 — 프레임 분할 사이에 listening이 꺼지거나 선택될 수 있음 (stale hit 방지)
              if (!entity.node.listening() || this.isCacheExcluded(entity)) continue
              // 과대 엔티티는 건너뛰지 않고 비트맵 상한에 맞춘 배율로 캐시(계획에 반영됨)
              // (건너뛰면 매 프레임 벡터로 그려져 저사양 조작을 느리게 함 — 실기기 프로파일로 확인)
              this.markEntityStatic(entity, { pixelRatio: ratio })
            } catch (e) {
              console.warn(`Deferred cache failed: ${entity.getId()}`, e)
            }

            if (performance.now() - startTime > FRAME_BUDGET) return true
          }
          return false
        }
        // 캐시 굽기는 모양을 바꾸지 않는데 cache()마다 레이어 전체 재그리기가 예약돼, 층 진입 뒤 조각마다
        // 미캐시 엔티티를 벡터로 다시 그렸다(A21s 도형 많은 층: 13회 합 ≈0.8s) → 조각 중엔 예약을 끄고 끝에 한 번 그린다
        const more = this.renderer.hints.withoutAutoDraw(runSlice)
        if (more) {
          requestAnimationFrame(execute)
          return
        }

        this.renderer.requestDraw(this.renderer.layers.background)
        if (!onlyUncached) this.isEntityTransformed = false
        // 첫 제스처가 멈추지 않도록 브러시 스냅샷을 미리 준비
        this.prepareBrushSnapshot()
        resolve()
      }

      requestAnimationFrame(execute)
    })
  }

  public async removeTargetCache(entities: CanvasEngineEntity[]): Promise<void> {
    if (entities.length === 0) return
    await Promise.all(
      entities.map(async entity => {
        this.unmarkEntityStatic(entity)
      }),
    )
  }

  /**
   * 특정 엔티티 배열을 받아서 순차적으로 배치 캐싱을 수행합니다.
   * @param entities 캐싱을 복구할 엔티티 배열
   */
  public async restoreTargetCache(entities: CanvasEngineEntity[]): Promise<void> {
    if (entities.length === 0) return
    // 1. 배치 설정 (refreshCache와 동일하게 5개 단위 권장)
    const BATCH_SIZE = this.cacheBatchSize
    const totalEntities = entities.length

    for (let i = 0; i < totalEntities; i += BATCH_SIZE) {
      const chunk = entities.slice(i, i + BATCH_SIZE)

      // 2. 현재 청크 배치 처리
      await Promise.all(
        chunk.map(async entity => {
          try {
            // listening(false) 상태에서 구운 캐시는 hit이 비어 선택 불가(stale)가 되므로 스킵
            if (!entity.node.listening()) return
            if (!this.isCacheableEntity(entity)) return
            // 기존 캐시가 있다면 정리 후 새로 생성 (클래스 공통 품질 설정 사용)
            this.markEntityStatic(entity)
          } catch (error) {
            console.warn(`엔티티 캐시 복구 실패: ${entity.getId()}`, error)
          }
        }),
      )

      // 3. [핵심] 브라우저에게 제어권을 넘겨 UI 렌더링 기회 제공
      // 다음 배치 작업 전에 화면을 한 번 그리게 하여 쾌적한 UX 제공
      if (i + BATCH_SIZE < totalEntities) {
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
    }

    // 모든 배치가 끝나면 레이어 최종 업데이트
    this.renderer.requestDraw(this.renderer.layers.background)
  }
  // 내부 이벤트 관리 -----------------------------------------------------------------------------------------------
  protected __disableListeneingIds: string[] = []
  disableEntityListening(): void {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (entity.node.listening() && !this.__disableListeneingIds.includes(entity.getId())) {
        entity.node.listening(false)
        this.__disableListeneingIds.push(entity.getId())
      }
    })
  }

  // ---- 경계 API: 바깥(스토어·컴포넌트)이 Konva 레이어/노드를 직접 만지지 않게 하는 래퍼 ----

  /**
   * 엔티티 레이어를 다시 그린다.
   * @param immediate true면 동기 draw(로딩 중 GPU 워밍업처럼 즉시 래스터가 필요할 때), 기본은 batchDraw
   */
  redraw(immediate: boolean = false): void {
    if (immediate) this.renderer.drawNow(this.renderer.layers.background)
    else this.renderer.requestDraw(this.renderer.layers.background)
  }

  /** 브러시 엔티티 전체 표시/숨김 (브러시 숨기기 토글) */
  setBrushEntitiesVisible(visible: boolean): void {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (!entity.isBrush()) return
      if (visible) entity.node.show()
      else entity.node.hide()
    })
  }

  /**
   * 전 엔티티의 비트맵 캐시를 무효화한다(다음 batchDraw가 라이브 드로우).
   * 재캐시는 기존 배치 경로(refreshCache/refreshAsyncCache/deferredCacheEntities)가 수행한다.
   */
  clearAllEntityCache(): void {
    this.entities.forEach((entity: CanvasEngineEntity) => entity.invalidateCache())
  }

  disableAnotherEntityListening(id: string): void {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (
        entity.node.listening() &&
        !this.__disableListeneingIds.includes(entity.getId()) &&
        entity.getId() != id
      ) {
        entity.node.listening(false)
        this.__disableListeneingIds.push(entity.getId())
      }
    })
  }

  restoreEntitiyListening(): void {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (this.__disableListeneingIds.includes(entity.getId())) {
        entity.node.listening(true)
        this.__disableListeneingIds = this.__disableListeneingIds.filter(
          (id: string) => id !== entity.getId(),
        )
      }
    })
  }
  restoreAllEntitiyListening(): void {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      entity.node.listening(true)
      this.__disableListeneingIds = this.__disableListeneingIds.filter(
        (id: string) => id !== entity.getId(),
      )
    })
  }

  setAllFloorNumbering(isAllFloorNumbering: boolean): void {
    this.isAllFloorNumbering = isAllFloorNumbering
  }

  setAllFloorIndex(index: number): void {
    this.allFloorIndex = index
  }

  setBalloonScaleGlobal(scale: number, onlySet: boolean = false): void {
    this.globalBalloonScale = scale
    if (onlySet) {
      return
    }
    this.entities.forEach((entity: CanvasEngineEntity) => {
      // balloon 크기와 테두리 굵기만 변경 (connectedLine, shape 제외)
      entity.applyShapeScale(scale)
    })
    // 레이어 다시 그리기
    this.renderer.requestDraw(this.renderer.layers.background)
  }

  setConnectedLineThicknessScaleGlobal(scale: number, onlySet: boolean = false): void {
    this.globalConnectedLineThicknessScale = scale
    if (onlySet) {
      return
    }
    this.entities.forEach((entity: CanvasEngineEntity) => {
      entity.applyConnectedLineSize()
      // 방향도형인 경우 선 굵기 업데이트
      if (entity.entityType === SHAPES.DIRECTION) {
        entity.applyAreaLineWidthDirection()
        // 캐시 무효화하여 즉시 시각적 업데이트
      }
    })
    // 레이어 다시 그리기
    this.renderer.requestDraw(this.renderer.layers.background)
  }

  setBalloonFontScaleGlobal(scale: number, onlySet: boolean = false): void {
    this.globalBalloonFontScale = scale
    if (onlySet) {
      return
    }
    this.entities.forEach((entity: CanvasEngineEntity) => {
      entity.applyBalloonFontSize()
    })
    // 레이어 다시 그리기
    this.renderer.requestDraw(this.renderer.layers.background)
  }

  setPointShapeScaleGlobal(scale: number, onlySet: boolean = false): void {
    this.globalPointShapeScale = scale
    if (onlySet) {
      return
    }
    this.entities.forEach((entity: CanvasEngineEntity) => {
      entity.applyPointSize()
    })
    // 레이어 다시 그리기
    this.renderer.requestDraw(this.renderer.layers.background)
  }

  setAreaBorderThicknessScaleGlobal(scale: number, onlySet: boolean = false): void {
    if (onlySet) {
      return
    }
    this.globalAreaBorderThicknessScale = scale
  }

  setBalloonFontColorGlobal(color: string): void {
    this.globalBalloonFontColor = color
  }
  setBalloonFillColorGlobal(color: string): void {
    this.globalBalloonFillColor = color
  }
  setBalloonBorderColorGlobal(color: string): void {
    this.globalBalloonBorderColor = color
  }
  setBalloonFillOpacityGlobal(opacity: number): void {
    this.globalBalloonFillOpacity = opacity
  }
  /** 배경 이미지 노드 파괴 — 사전 디코드한 ImageBitmap(원본 크기 픽셀 상주)은 GC를 기다리지 않고 즉시 해제 */
  protected destroyBackgroundImage(): void {
    const background = this.backgroundImage
    if (!background) return
    const source = background.image()
    background.destroy()
    this.backgroundImage = undefined
    if (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap) source.close()
  }

  destroy(): void {
    // 제스처 중 파괴면 분리된 홀더의 브러시를 레이어로 먼저 원복(아래 이벤트 정리·레이어 파괴가 함께 처리하도록)
    this.endGestureBrushCache()
    if (this._cacheResTimer) {
      clearTimeout(this._cacheResTimer)
      this._cacheResTimer = null
    }
    if (this.__wheelResumeTimer) {
      clearTimeout(this.__wheelResumeTimer)
      this.__wheelResumeTimer = null
    }
    window.removeEventListener('pointerdown', this.flushWheelGesture, true)
    window.removeEventListener('mousedown', this.flushWheelGesture, true)
    window.removeEventListener('touchstart', this.flushWheelGesture, true)
    // 1. 이벤트 매니저에서 이 스테이지와 관련된 모든 참조 제거 (가장 먼저!)
    if (this.stage) {
      this.eventManager.removeAllEvents(this.renderer.stage)

      // 2. 모든 하위 노드에 대해서도 이벤트 제거 (안전 장치)
      //    — eventManager 등록은 전부 핸들로 하므로 참조 비교가 맞도록 핸들로 순회한다
      this.renderer.layers.background
        .getChildren()
        .forEach(node => this.eventManager.removeAllEvents(node))
      this.renderer.layers.system
        .getChildren()
        .forEach(node => this.eventManager.removeAllEvents(node))
    }

    // 3. 렌더러 객체 파괴 (레이어 → 스테이지 순, 원본과 동일)
    if (this.stage) {
      this.destroyBackgroundImage()
      this.renderer.layers.background.destroy()
      this.renderer.layers.system.destroy()
      this.renderer.destroy()
    }

    // 4. 강제 참조 해제
    this.entities = []
    this.stage = null
  }
}
